import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PageMenu from "components/blocks/PageMenu/PageMenu";
import Icon from "components/blocks/Icon/Icon";
import LoaderMin from "components/blocks/Loader/LoaderMin";
import NoData from "components/pages/noData/NoData";
import IconArrowGray from "img/icons/svg/botones/flecha-big-gris.svg";
import useDecode from "hooks/useDecode";
import useGetAllAdministrationRecord from "services/recordSheets/useGetAllAdministrationRecord";
import useGetAllEconomicRecord from "services/recordSheets/useGetAllEconomicRecord";
import "components/blocks/ManagementItemBlock/ManagementItemBlock.scss";

interface ManagementComponent {
  id: string;
  nombre: string | null;
  fecha: string | null;
  empresa: string | null;
  tipo_componente: number;
}

interface ManagementGroup {
  ComponenteNombre: string;
  Count: number;
  Componentes: ManagementComponent[];
}

interface GroupState {
  groupName?: string;
  components?: ManagementComponent[];
  iconName?: "calendar" | "euro" | "shield";
  variant?: "blue" | "amber" | "green";
}

const RecordDetailManagementGroup: React.FC = () => {
  const { id, managementType, componentType } = useParams<{
    id: string;
    managementType: string;
    componentType: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as GroupState;

  const [groups, setGroups] = useState<ManagementGroup[] | null>(null);
  const tokenResponse = useDecode("token");
  const userId = tokenResponse && tokenResponse.extension_id;

  const arrParamsId = (id || "").split("_");
  const recordId = arrParamsId[1];
  const typeId = Number(componentType);

  const { getAllAdministrationRecord, loadingGetAllAdministrationRecord } = useGetAllAdministrationRecord();
  const { getAllEconomicRecord, loadingGetAllEconomicRecord } = useGetAllEconomicRecord();

  const isEconomic = (managementType || "").toLowerCase().includes("econ");
  const iconName = state.iconName || (isEconomic ? "euro" : "calendar");
  const variantClass = isEconomic ? "mgmt-card--amber" : "";
  const iconClass = isEconomic ? "mgmt-card__icon--amber" : "";

  useEffect(() => {
    if (Array.isArray(state.components) && state.components.length > 0) {
      return;
    }

    if (!userId || !recordId) return;

    if (isEconomic) {
      getAllEconomicRecord(userId, recordId, setGroups);
      return;
    }

    getAllAdministrationRecord(userId, recordId, setGroups);
  }, [isEconomic, userId, recordId]);

  const componentList = useMemo(() => {
    if (Array.isArray(state.components) && state.components.length > 0) {
      return state.components.filter((component) => Number(component.tipo_componente) === typeId);
    }

    if (!Array.isArray(groups)) return [];

    const match = groups.find((group) =>
      Array.isArray(group.Componentes) &&
      group.Componentes.some((component) => Number(component.tipo_componente) === typeId)
    );

    return (match?.Componentes || []).filter((component) => Number(component.tipo_componente) === typeId);
  }, [state.components, groups, typeId]);

  const groupTitle = useMemo(() => {
    if (state.groupName) return state.groupName;

    if (!Array.isArray(groups)) return "Detalle de componentes";

    const match = groups.find((group) =>
      Array.isArray(group.Componentes) &&
      group.Componentes.some((component) => Number(component.tipo_componente) === typeId)
    );

    return match?.ComponenteNombre || "Detalle de componentes";
  }, [state.groupName, groups, typeId]);

  const isLoading = loadingGetAllAdministrationRecord || loadingGetAllEconomicRecord;

  return (
    <>
      <PageMenu title={groupTitle} type="bottom-box-info" breadcrumbs={["Historial", "Gestión", groupTitle]}>
        <div className="btns-header-container">
          <Link to={`/historial/${id}`}>
            <img src={IconArrowGray} alt="icon arrow right" />
          </Link>
        </div>
      </PageMenu>

      <section>
        <div className="container">
          {isLoading && componentList.length === 0 ? (
            <LoaderMin />
          ) : componentList.length === 0 ? (
            <NoData message="No hay componentes para mostrar." />
          ) : (
            <div className="mgmt-items">
              {componentList.map((item) => (
                <div key={item.id} className={`mgmt-card ${variantClass}`}>
                  <span className={`mgmt-card__icon${iconClass ? ` ${iconClass}` : ""}`}>
                    <Icon name={iconName} size={16} />
                  </span>
                  <div className="mgmt-card__content">
                    <div className="mgmt-card__name">{item.nombre}</div>
                    {item.empresa && <div className="mgmt-card__empresa">{item.empresa}</div>}
                    {item.fecha && item.fecha !== "0001-01-01T00:00:00" && (
                      <div className="mgmt-card__date">{new Date(item.fecha).toLocaleDateString("es-ES")}</div>
                    )}
                  </div>
                  <div className="mgmt-card__actions">
                    <button
                      className="mgmt-card__view-btn"
                      type="button"
                      onClick={() => navigate(`/historial/${id}/${managementType}/${item.tipo_componente}__${item.id}`)}
                    >
                      <Icon name="eye" size={14} />
                      Ver
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default RecordDetailManagementGroup;